const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getToken = () => localStorage.getItem("aeva_token");
const setToken = (t) => localStorage.setItem("aeva_token", t);
const clearToken = () => localStorage.removeItem("aeva_token");

async function req(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth && getToken()) headers.Authorization = `Bearer ${getToken()}`;
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try { const e = await res.json(); detail = e.detail || detail; } catch {}
    const err = new Error(typeof detail === "string" ? detail : "Request failed");
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const api = {
  getToken, setToken, clearToken,
  signup: (d) => req("/auth/signup", { method: "POST", body: d, auth: false }),
  login: (d) => req("/auth/login", { method: "POST", body: d, auth: false }),
  me: () => req("/auth/me"),
  forgotPassword: (email) => req("/auth/forgot-password", { method: "POST", body: { email, origin: window.location.origin }, auth: false }),
  resetPassword: (token, password) => req("/auth/reset-password", { method: "POST", body: { token, password }, auth: false }),
  submitContact: (d) => req("/contact", { method: "POST", body: d, auth: false }),
  adminReviews: () => req("/admin/reviews"),
  adminStats: () => req("/admin/stats"),
  adminPlans: () => req("/admin/plans"),
  adminNotifStats: () => req("/admin/notifications/stats"),
  toggleReview: (id) => req(`/admin/reviews/${id}/toggle`, { method: "POST" }),
  deleteReview: (id) => req(`/admin/reviews/${id}`, { method: "DELETE" }),
  broadcast: (payload) => req("/admin/broadcast", { method: "POST", body: payload }),
  adminBroadcasts: () => req("/admin/broadcasts"),
  cancelBroadcast: (id) => req(`/admin/broadcasts/${id}`, { method: "DELETE" }),
  installFunnel: () => req("/admin/install-funnel"),
  importMembers: (members, comp_days, send_email) => req("/admin/import-members", { method: "POST", body: { members, comp_days, send_email, origin: window.location.origin } }),
  updateMe: (p) => req("/me", { method: "PUT", body: p }),
  dismissNudge: (key) => req("/me/dismiss-nudge", { method: "POST", body: { key } }),
  gentleSpace: () => req("/gentle-space"),
  gentleStart: (babyName, tone) => req("/gentle-space/start", { method: "POST", body: { babyName, tone } }),
  gentleComfort: () => req("/gentle-space/comfort", { method: "POST", body: {} }),
  gentleTone: (tone) => req("/gentle-space/tone", { method: "POST", body: { tone } }),
  gentleClose: () => req("/gentle-space/close", { method: "POST", body: {} }),
  checkin: () => req("/checkin", { method: "POST" }),
  freezeStreak: () => req("/streak/freeze", { method: "POST" }),
  publicStats: () => req("/public/stats", { auth: false }),
  publicMap: () => req("/public/map", { auth: false }),
  publicRecent: () => req("/public/recent", { auth: false }),
  coachDemo: (coach_id, message) => req("/public/coach-demo", { method: "POST", body: { coach_id, message }, auth: false }),
  lumiDemo: (weeks, role) => req("/public/lumi-demo", { method: "POST", body: { weeks, role }, auth: false }),
  coaches: () => req("/coaches", { auth: false }),
  coachHistory: (id, sid) => req(`/coaches/${id}/history?session_id=${sid}`),
  coachChat: (id, sid, message) => req(`/coaches/${id}/chat`, { method: "POST", body: { session_id: sid, message } }),
  leaderboard: () => req("/leaderboard"),
  feed: () => req("/feed"),
  kudos: (id) => req(`/feed/${id}/kudos`, { method: "POST" }),
  createPost: (text, imageUrl) => req("/feed", { method: "POST", body: { text, imageUrl } }),
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/upload`, { method: "POST", headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {}, body: fd });
    if (!res.ok) { let d = "Upload failed"; try { d = (await res.json()).detail || d; } catch {} throw new Error(d); }
    return res.json();
  },
  comments: (id) => req(`/feed/${id}/comments`),
  addComment: (id, text) => req(`/feed/${id}/comments`, { method: "POST", body: { text } }),
  challenges: () => req("/challenges"),
  toggleChallenge: (id) => req(`/challenges/${id}/toggle`, { method: "POST" }),
  getSets: () => req("/workouts/sets"),
  addSet: (d) => req("/workouts/sets", { method: "POST", body: d }),
  delSet: (id) => req(`/workouts/sets/${id}`, { method: "DELETE" }),
  nutritionToday: () => req("/nutrition/today"),
  addMeal: (d) => req("/nutrition/meal", { method: "POST", body: d }),
  addWater: (delta) => req("/nutrition/water", { method: "POST", body: { delta } }),
  getWeight: () => req("/weight"),
  addWeight: (kg) => req("/weight", { method: "POST", body: { kg } }),
  dashboard: () => req("/dashboard"),
  trends: (days = 14) => req(`/trends?days=${days}`),
  weeklyReport: () => req("/weekly-report"),
  habits: () => req("/habits"),
  addHabit: (d) => req("/habits", { method: "POST", body: d }),
  toggleHabit: (id) => req(`/habits/${id}/toggle`, { method: "POST" }),
  delHabit: (id) => req(`/habits/${id}`, { method: "DELETE" }),
  goals: () => req("/goals"),
  addGoal: (d) => req("/goals", { method: "POST", body: d }),
  delGoal: (id) => req(`/goals/${id}`, { method: "DELETE" }),
  journal: () => req("/journal"),
  addJournal: (d) => req("/journal", { method: "POST", body: d }),
  getCycle: () => req("/cycle"),
  saveCycle: (d) => req("/cycle", { method: "POST", body: d }),
  getPartner: () => req("/partner"),
  connectPartner: (code) => req("/partner/connect", { method: "POST", body: { code } }),
  partnerPrefs: (prefs) => req("/partner/prefs", { method: "POST", body: prefs }),
  disconnectPartner: () => req("/partner/disconnect", { method: "POST" }),
  nudgePartner: (kind, message) => req("/partner/nudge", { method: "POST", body: { kind, message } }),
  getPartnerNudges: () => req("/partner/nudges"),
  getLovedOnes: () => req("/loved-ones"),
  addLovedOne: (d) => req("/loved-ones", { method: "POST", body: d }),
  updateLovedOne: (id, d) => req(`/loved-ones/${id}`, { method: "PATCH", body: d }),
  deleteLovedOne: (id) => req(`/loved-ones/${id}`, { method: "DELETE" }),
  sendLovedOne: (id, message) => req(`/loved-ones/${id}/send`, { method: "POST", body: { message } }),
  getPregnancy: () => req("/pregnancy"),
  setPregnancy: (d) => req("/pregnancy", { method: "POST", body: d }),
  pregnancyBirth: (birthDate) => req("/pregnancy/birth", { method: "POST", body: { birthDate } }),
  pregnancyEnd: () => req("/pregnancy/end", { method: "POST" }),
  pregnancyInsight: (role) => req(`/pregnancy/insight?role=${role || "her"}`),
  partnerPregnancyInsight: () => req("/partner/pregnancy-insight"),
  getCheckin: () => req("/pregnancy/checkin"),
  submitCheckin: (answers) => req("/pregnancy/checkin", { method: "POST", body: { answers } }),
  setCheckinSettings: (d) => req("/pregnancy/checkin/settings", { method: "POST", body: d }),
  checkinHistory: () => req("/pregnancy/checkin/history"),
  cycleInsight: () => req("/cycle/insight"),
  reportPost: (id, reason) => req(`/feed/${id}/report`, { method: "POST", body: { reason } }),
  reportComment: (id, reason) => req(`/comments/${id}/report`, { method: "POST", body: { reason } }),
  blockUser: (id) => req(`/users/${id}/block`, { method: "POST" }),
  unblockUser: (id) => req(`/users/${id}/unblock`, { method: "POST" }),
  deleteAccount: () => req("/auth/account", { method: "DELETE" }),
  wearables: () => req("/wearables"),
  challengeLeaderboard: (cid) => req(`/challenges/${cid}/leaderboard`),
  hallOfFame: () => req("/hall-of-fame"),
  referrals: () => req("/referrals"),
  pushActivity: (data) => req("/wearables/activity", { method: "POST", body: data }),
  toggleWearable: (id) => req(`/wearables/${id}/toggle`, { method: "POST" }),
  family: () => req("/family"),
  familyInvite: (email, role) => req("/family/invite", { method: "POST", body: { email, role } }),
  familyRespond: (id, accept) => req(`/family/invite/${id}/respond`, { method: "POST", body: { accept } }),
  notifications: () => req("/notifications"),
  notificationsUnread: () => req("/notifications/unread"),
  markNotificationsRead: () => req("/notifications/read", { method: "POST" }),
  reviews: () => req("/reviews", { auth: false }),
  submitReview: (d) => req("/reviews", { method: "POST", body: d, auth: false }),
  cancelSubscription: () => req("/subscription/cancel", { method: "POST" }),
  registerPush: (token, platform) => req("/push/register", { method: "POST", body: { token, platform } }),
  pushTest: () => req("/push/test", { method: "POST", body: {} }),
  pushStatus: () => req("/push/status"),
  stravaStatus: () => req("/strava/status"),
  stravaAuthUrl: (redirectUri) => req(`/strava/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`),
  stravaCallback: (code) => req("/strava/callback", { method: "POST", body: { code } }),
  stravaSync: () => req("/strava/sync"),
  stravaDisconnect: () => req("/strava/disconnect", { method: "POST" }),
  searchFood: (q) => req(`/nutrition/search?q=${encodeURIComponent(q)}`),
  lookupBarcode: (code) => req(`/nutrition/barcode/${encodeURIComponent(code)}`),
  createCheckout: (planId, originUrl, annual) => req("/checkout/session", { method: "POST", body: { plan_id: planId, origin_url: originUrl, annual } }),
  checkoutStatus: (sessionId) => req(`/checkout/status/${sessionId}`),
  systemJobs: () => req("/system/jobs", { auth: false }),
};

export default api;
